import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatRp } from "@/lib/api";
import GlassCard from "@/components/GlassCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Server, CreditCard, Box, MapPin, Tag, Plus, Trash2, Wifi, Save, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettings() {
  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-heading text-4xl font-extrabold text-white">Admin Settings</h1>
        <p className="text-slate-400 mt-1">Konfigurasi Pterodactyl, Midtrans, Egg, Location & Paket.</p>
      </div>

      <Tabs defaultValue="integrations">
        <TabsList className="glass border border-cyan-500/10 flex-wrap h-auto">
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrasi</TabsTrigger>
          <TabsTrigger value="eggs" data-testid="tab-eggs">Eggs</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Locations</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-plans">Paket</TabsTrigger>
        </TabsList>
        <TabsContent value="integrations" className="mt-4"><Integrations /></TabsContent>
        <TabsContent value="eggs" className="mt-4"><EggsAdmin /></TabsContent>
        <TabsContent value="locations" className="mt-4"><LocationsAdmin /></TabsContent>
        <TabsContent value="plans" className="mt-4"><PlansAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function Integrations() {
  const { data, refetch } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState({ pterodactyl_url: "", pterodactyl_api_key: "", pterodactyl_client_key: "", midtrans_server_key: "", midtrans_client_key: "", midtrans_production: true });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (data) setForm((f) => ({ ...f, pterodactyl_url: data.pterodactyl_url || "", midtrans_client_key: data.midtrans_client_key || "", midtrans_production: data.midtrans_production }));
  }, [data]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "" ) delete payload[k]; });
      await api.put("/settings", payload);
      toast.success("Settings disimpan");
      refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setSaving(false); }
  };

  const testPtero = async () => {
    setTesting(true);
    try { const { data } = await api.post("/settings/test-pterodactyl"); data.ok ? toast.success(data.message) : toast.error(data.message); }
    catch (e) { toast.error("Gagal test koneksi"); }
    finally { setTesting(false); }
  };

  const field = (k, placeholder) => (
    <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
      placeholder={placeholder} data-testid={`setting-${k}`}
      className="bg-slate-900/60 border-cyan-500/20 text-white" />
  );

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><Server className="text-cyan-400" size={18} /> Pterodactyl Panel</h3>
          {data && (data.pterodactyl_configured
            ? <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 size={14} /> Terkonfigurasi</span>
            : <span className="text-xs text-slate-500 flex items-center gap-1"><XCircle size={14} /> Belum diset</span>)}
        </div>
        <div className="space-y-3">
          <div><Label className="text-slate-300 text-sm">Panel URL</Label>{field("pterodactyl_url", "https://panel.domainkamu.com")}</div>
          <div><Label className="text-slate-300 text-sm">Application API Key</Label>{field("pterodactyl_api_key", data?.pterodactyl_api_key_masked || "ptla_...")}</div>
          <div><Label className="text-slate-300 text-sm">Client API Key (untuk power actions)</Label>{field("pterodactyl_client_key", data?.pterodactyl_client_key_masked || "ptlc_...")}</div>
          <Button onClick={testPtero} disabled={testing} variant="outline" className="border-cyan-500/30 bg-slate-900/60 text-cyan-300 hover:bg-cyan-600 hover:text-white w-full" data-testid="test-ptero-btn">
            {testing ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Wifi size={15} className="mr-2" />} Test Koneksi
          </Button>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-white flex items-center gap-2"><CreditCard className="text-cyan-400" size={18} /> Midtrans</h3>
          {data && (data.midtrans_configured
            ? <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle2 size={14} /> Terkonfigurasi</span>
            : <span className="text-xs text-slate-500 flex items-center gap-1"><XCircle size={14} /> Belum diset</span>)}
        </div>
        <div className="space-y-3">
          <div><Label className="text-slate-300 text-sm">Server Key</Label>{field("midtrans_server_key", data?.midtrans_server_key_masked || "Mid-server-...")}</div>
          <div><Label className="text-slate-300 text-sm">Client Key</Label>{field("midtrans_client_key", "Mid-client-...")}</div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900/50">
            <span className="text-sm text-slate-300">Mode Production</span>
            <Switch checked={form.midtrans_production} onCheckedChange={(v) => setForm({ ...form, midtrans_production: v })} data-testid="midtrans-production-switch" />
          </div>
        </div>
      </GlassCard>

      <div className="lg:col-span-2">
        <Button onClick={save} disabled={saving} className="btn-cyan text-white border-0 w-full h-11" data-testid="save-settings-btn">
          {saving ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />} Simpan Semua Settings
        </Button>
      </div>
    </div>
  );
}

function EggsAdmin() {
  const { data, refetch } = useQuery({ queryKey: ["eggs"], queryFn: async () => (await api.get("/eggs")).data });
  const [form, setForm] = useState({ name: "", egg_id: "", nest_id: 1, docker_image: "", startup: "" });

  const add = async () => {
    if (!form.name || !form.egg_id) return toast.error("Nama & Egg ID wajib diisi");
    try {
      await api.post("/eggs", { ...form, egg_id: Number(form.egg_id), nest_id: Number(form.nest_id), environment: {}, enabled: true });
      toast.success("Egg ditambahkan"); setForm({ name: "", egg_id: "", nest_id: 1, docker_image: "", startup: "" }); refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { await api.delete(`/eggs/${id}`); toast.success("Dihapus"); refetch(); };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <GlassCard className="p-6 lg:col-span-1 h-fit">
        <h3 className="font-heading font-bold text-white flex items-center gap-2 mb-4"><Plus size={18} className="text-cyan-400" /> Tambah Egg</h3>
        <div className="space-y-3">
          <Input placeholder="Nama (Minecraft - Paper)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="egg-name-input" />
          <Input placeholder="Egg ID" type="number" value={form.egg_id} onChange={(e) => setForm({ ...form, egg_id: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="egg-id-input" />
          <Input placeholder="Nest ID" type="number" value={form.nest_id} onChange={(e) => setForm({ ...form, nest_id: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="egg-nest-input" />
          <Input placeholder="Docker Image" value={form.docker_image} onChange={(e) => setForm({ ...form, docker_image: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="egg-docker-input" />
          <Input placeholder="Startup Command" value={form.startup} onChange={(e) => setForm({ ...form, startup: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="egg-startup-input" />
          <Button onClick={add} className="btn-cyan text-white border-0 w-full" data-testid="add-egg-btn"><Plus size={15} className="mr-2" /> Tambah</Button>
        </div>
      </GlassCard>
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3 h-fit">
        {(data || []).map((e) => (
          <GlassCard key={e.id} className="p-4 flex items-start justify-between" data-testid={`egg-item-${e.egg_id}`}>
            <div className="flex items-start gap-3">
              <Box className="text-cyan-400 mt-0.5" size={18} />
              <div><div className="text-white font-semibold text-sm">{e.name}</div><div className="text-xs text-slate-500 font-mono">ID {e.egg_id} · Nest {e.nest_id}</div></div>
            </div>
            <button onClick={() => del(e.id)} className="text-rose-400 hover:text-rose-300" data-testid={`del-egg-${e.egg_id}`}><Trash2 size={16} /></button>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

function LocationsAdmin() {
  const { data, refetch } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/locations")).data });
  const [form, setForm] = useState({ name: "", code: "", location_id: "" });
  const add = async () => {
    if (!form.name || !form.code || !form.location_id) return toast.error("Semua field wajib diisi");
    try { await api.post("/locations", { ...form, location_id: Number(form.location_id), enabled: true }); toast.success("Location ditambahkan"); setForm({ name: "", code: "", location_id: "" }); refetch(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const del = async (id) => { await api.delete(`/locations/${id}`); toast.success("Dihapus"); refetch(); };

  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <GlassCard className="p-6 h-fit">
        <h3 className="font-heading font-bold text-white flex items-center gap-2 mb-4"><Plus size={18} className="text-cyan-400" /> Tambah Location</h3>
        <div className="space-y-3">
          <Input placeholder="Nama (Jakarta - Indonesia)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="loc-name-input" />
          <Input placeholder="Kode (JKT)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="loc-code-input" />
          <Input placeholder="Location ID" type="number" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })} className="bg-slate-900/60 border-cyan-500/20 text-white" data-testid="loc-id-input" />
          <Button onClick={add} className="btn-cyan text-white border-0 w-full" data-testid="add-loc-btn"><Plus size={15} className="mr-2" /> Tambah</Button>
        </div>
      </GlassCard>
      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-3 h-fit">
        {(data || []).map((l) => (
          <GlassCard key={l.id} className="p-4 flex items-start justify-between" data-testid={`loc-item-${l.location_id}`}>
            <div className="flex items-start gap-3"><MapPin className="text-cyan-400 mt-0.5" size={18} /><div><div className="text-white font-semibold">{l.code}</div><div className="text-xs text-slate-500">{l.name} · ID {l.location_id}</div></div></div>
            <button onClick={() => del(l.id)} className="text-rose-400 hover:text-rose-300" data-testid={`del-loc-${l.location_id}`}><Trash2 size={16} /></button>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}

function PlansAdmin() {
  const { data, refetch } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/plans")).data });
  const save = async (p) => { try { await api.put(`/plans/${p.id}`, p); toast.success("Paket disimpan"); refetch(); } catch { toast.error("Gagal"); } };

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {(data || []).map((p) => <PlanEditor key={p.id} plan={p} onSave={save} />)}
    </div>
  );
}

function PlanEditor({ plan, onSave }) {
  const [p, setP] = useState(plan);
  useEffect(() => setP(plan), [plan]);
  return (
    <GlassCard className="p-5" data-testid={`plan-edit-${plan.ram_gb}gb`}>
      <div className="font-heading font-bold text-white text-xl mb-3">{p.ram_gb}GB</div>
      <div className="space-y-2">
        <div><Label className="text-xs text-slate-400">Harga (Rp)</Label><Input type="number" value={p.price} onChange={(e) => setP({ ...p, price: Number(e.target.value) })} className="bg-slate-900/60 border-cyan-500/20 text-white h-9" data-testid={`plan-price-${plan.ram_gb}`} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-xs text-slate-400">vCore</Label><Input type="number" value={p.vcore} onChange={(e) => setP({ ...p, vcore: Number(e.target.value) })} className="bg-slate-900/60 border-cyan-500/20 text-white h-9" /></div>
          <div><Label className="text-xs text-slate-400">Disk GB</Label><Input type="number" value={p.disk_gb} onChange={(e) => setP({ ...p, disk_gb: Number(e.target.value) })} className="bg-slate-900/60 border-cyan-500/20 text-white h-9" /></div>
        </div>
        <div className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50">
          <span className="text-xs text-slate-300">Free & Enabled</span>
          <div className="flex gap-2">
            <Switch checked={p.is_free} onCheckedChange={(v) => setP({ ...p, is_free: v })} data-testid={`plan-free-${plan.ram_gb}`} />
            <Switch checked={p.enabled} onCheckedChange={(v) => setP({ ...p, enabled: v })} />
          </div>
        </div>
        <Button onClick={() => onSave(p)} className="btn-cyan text-white border-0 w-full h-9" data-testid={`save-plan-${plan.ram_gb}`}><Save size={14} className="mr-1" /> Simpan</Button>
      </div>
    </GlassCard>
  );
}
